import withQuery from 'with-query';

import { V1Coupon } from '../py-generated/datalouder/checkout/types';
import {
  BreakDownMembershipResponse,
  OverrideSubscriptionRequest,
} from '../py-generated/datalouder/membership/types';
import { Category } from '../py-generated/datalouder/stock/types';
import { UserInfoByAdmin } from '../py-generated/datalouder/user/admin_types';
import { WhatsAppChannelRequestsResponse } from '../py-generated/datalouder/whatsapp_channel/types';
import { WhatsAppChannelMembersResponse } from '../py-generated/datalouder/whatsapp_channel/types';
import { apiUrl, apiv2Url } from './config';
import { getOption, getPromise, postPromise, RequestOptions } from './utils';

const listUsers = (): Promise<UserInfoByAdmin[]> =>
  getPromise(`${apiv2Url}/users/admin`, {});

const listWhatsappMembers = (): Promise<WhatsAppChannelMembersResponse> =>
  getPromise(`${apiv2Url}/admin/whatsChannelAppMembers`, {});

const listWhatsappMemberRequests =
  (): Promise<WhatsAppChannelRequestsResponse> =>
    getPromise(`${apiv2Url}/admin/whatsAppMembersRequests`, {});

const listMembershipsOptions = (): RequestOptions => ({
  url: `${apiv2Url}/membership/admin/memberships`,
  ...getOption(),
});

const listCommunicationRecordsOptions = (): RequestOptions => ({
  url: `${apiv2Url}/communication_records/admin`,
  ...getOption(),
});

const getMembershipOverrideOptions = (userId: number): RequestOptions => ({
  url: withQuery(`${apiv2Url}/membership/admin/override`, { userId }),
  ...getOption(),
});

const breakdownMembership = (
  userId: number
): Promise<BreakDownMembershipResponse> =>
  getPromise<{ userId: number }, BreakDownMembershipResponse>(
    `${apiv2Url}/membership/admin/breakdown`,
    { userId }
  );

const setUpdateWhatsAppMemberRequest = (
  userId: number,
  requestId: string,
  done: boolean,
  remark: string,
  staffRemark: string
): Promise<{ url: string }> =>
  postPromise(`${apiv2Url}/admin/whatsAppMemberRequest`, {
    user_id: userId,
    request_id: requestId,
    done,
    remark,
    staff_remark: staffRemark,
  });

const addNewWhatsAppMember = (
  loginId: string,
  whatsAppNumber: string
): Promise<{ url: string }> =>
  postPromise(`${apiv2Url}/admin/whatsAppChannelMember`, {
    login: loginId,
    whatsapp_number: whatsAppNumber,
  });

const setUpdateWhatsAppMemberData = (
  userId: number,
  whatsappNumber: string,
  status: string
): Promise<{ url: string }> =>
  postPromise(`${apiv2Url}/admin/whatsAppMember`, {
    user_id: userId,
    whatsapp_number: whatsappNumber,
    status,
  });

const setMembershipOverride = (
  userId: number,
  reason: string,
  overrideType: string,
  ttl?: number
): Promise<{ url: string }> =>
  postPromise(`${apiv2Url}/membership/admin/override`, {
    userId,
    overrideType,
    reason,
    ttl,
  });

const setSubscriptionOverride = (
  userId: number,
  subscriptionId: string,
  provider: OverrideSubscriptionRequest['provider'],
  reason: string,
  expirationTime: number
): Promise<void> =>
  postPromise<OverrideSubscriptionRequest, void>(
    `${apiv2Url}/membership/admin/subscription-override`,
    {
      user_id: userId,
      subscription_id: subscriptionId,
      reason,
      expiration_time: expirationTime,
      provider,
    }
  );

const listKols = (): Promise<V1Coupon[]> =>
  postPromise(`${apiv2Url}/coupon/admin`, {});

const updateKols = (newCoupons: V1Coupon[]): Promise<void> =>
  postPromise(`${apiv2Url}/coupon/admin`, { coupons: newCoupons });

const listTasksOptions = (): RequestOptions => ({
  url: `${apiUrl}/admin/listTasks`,
  ...getOption(),
});

const listJobsOptions = (): RequestOptions => ({
  url: `${apiUrl}/admin/listJobs`,
  ...getOption(),
});

const listJobStatusesOptions = (jobId: number): RequestOptions => ({
  url: withQuery(`${apiUrl}/admin/listJobStatuses`, { jobId }),
  ...getOption(),
});

interface RestartResponse {
  stdout: string;
  stderr: string;
}

const restartRealtimeDaemon = (): Promise<RestartResponse> =>
  postPromise(`${apiUrl}/admin/restartRealtimeDaemon`, {}, data => data);

const restartBackendDaemon = (): Promise<RestartResponse> =>
  postPromise(`${apiUrl}/admin/restartBackendDaemon`, {}, data => data);

const restartMysqlDaemon = (): Promise<RestartResponse> =>
  postPromise(`${apiUrl}/admin/restartMysqlDaemon`, {}, data => data);

const restartCmsDaemon = (): Promise<RestartResponse> =>
  postPromise(`${apiUrl}/admin/restartCmsDaemon`, {}, data => data);

const getLog = (logStreamName: string): Promise<any> =>
  getPromise<any, any>(`${apiUrl}/admin/getLog`, { logStreamName });

const rescheduleJob = (
  abortSignal: AbortSignal,
  jobId: number
): Promise<void> =>
  postPromise(
    `${apiUrl}/admin/rescheduleJob`,
    { jobId },
    undefined,
    abortSignal
  );

const listFinancialOptions = (): RequestOptions => ({
  url: `${apiUrl}/admin/listFinancial`,
  ...getOption(),
});

const countPaymentsOptions = (): RequestOptions => ({
  url: `${apiv2Url}/membership/admin/count-payments`,
  ...getOption(),
});

const listUpcomingBillsOptions = (): RequestOptions => ({
  url: `${apiv2Url}/membership/admin/upcoming-bills`,
  ...getOption(),
});

const listPromoCodeUsageOptions = (): RequestOptions => ({
  url: `${apiv2Url}/membership/admin/stripePromoCodeUsage`,
  ...getOption(),
});

const queryAnalyticsOptions = (userId: number): RequestOptions => ({
  url: withQuery(`${apiv2Url}/admin/analytics`, { userId }),
  ...getOption(),
});

const getSubscriptionInsight = (): Promise<Record<string, number>> =>
  getPromise(`${apiv2Url}/membership/admin/subscription-insight`, {});

const getAWSLoginURL = (
  totp: string,
  redirect: string
): Promise<{
  url: string;
  sessionId: string;
  sessionKey: string;
  sessionToken: string;
}> => postPromise(`${apiv2Url}/aws_login`, { totp, redirect }, data => data);

const sendMessage = (message: Record<string, unknown>): Promise<void> =>
  postPromise(`${apiv2Url}/ws/message`, {
    message,
  });

const setIPODate = (
  category: Category,
  ticker: string,
  date: string
): Promise<void> =>
  postPromise(`${apiv2Url}/stocks/admin/ipo_date`, {
    category,
    ticker,
    date,
  });

export default {
  listUsers,
  listMembershipsOptions,
  listCommunicationRecordsOptions,
  getMembershipOverrideOptions,
  setMembershipOverride,
  listKols,
  updateKols,
  listTasksOptions,
  listJobsOptions,
  listJobStatusesOptions,
  listPromoCodeUsageOptions,
  listWhatsappMembers,
  listWhatsappMemberRequests,
  setUpdateWhatsAppMemberRequest,
  restartRealtimeDaemon,
  setUpdateWhatsAppMemberData,
  addNewWhatsAppMember,
  restartBackendDaemon,
  restartMysqlDaemon,
  restartCmsDaemon,
  getLog,
  rescheduleJob,
  listFinancialOptions,
  listUpcomingBillsOptions,
  queryAnalyticsOptions,
  getAWSLoginURL,
  sendMessage,
  countPaymentsOptions,
  breakdownMembership,
  setSubscriptionOverride,
  setIPODate,
  getSubscriptionInsight,
};
